import { define } from './define';

// Scheduling and following a publication: the schedule dialog with its text editor and the network's settings, moving,
// putting back on the calendar, retrying, the history of attempts, publishing by hand, and the small notes and badges a
// publication row carries. Keys start with "publications.".
export const publications = define({
  es: {
    // ── schedule ──
    'publications.schedule.title': 'Programar publicación',
    'publications.schedule.submit': 'Programar',
    'publications.schedule.submitting': 'Programando…',
    'publications.schedule.doneAuto': 'Programada: la publicará la app',
    'publications.schedule.doneManual': 'Programada: tocará publicarla a mano',
    'publications.schedule.blocked': { one: '{count} cosa impide publicar', other: '{count} cosas impiden publicar' },
    'publications.schedule.warnings': { one: '{count} aviso', other: '{count} avisos' },
    'publications.schedule.blockedHint': 'Arregla lo que impide publicar, o elige publicarla a mano',
    'publications.schedule.needsWhen': 'Elige la fecha y la hora',

    'publications.account.title': 'Cuenta',
    'publications.account.hint': 'Solo salen las cuentas para las que se aprobó esta versión.',
    'publications.account.none': 'Esta versión no está aprobada para ninguna cuenta todavía. Apruébala para una cuenta y podrás programarla.',
    'publications.account.auto': 'Automática',
    'publications.account.manual': 'A mano',
    'publications.account.reconnect': 'Hay que reconectarla',

    'publications.plan.autoTitle': 'La publica la app',
    'publications.plan.autoAs': 'como {placement}',
    'publications.plan.autoBody': 'Prepara los archivos poco antes de la hora y después comprueba que el post ha salido.',
    'publications.plan.manualTitle': 'La publica una persona',
    'publications.plan.manualBody': 'A su hora aparece en «Publicar hoy», con los archivos y el texto listos para copiar.',
    'publications.plan.checking': 'Comprobando cómo saldría…',
    'publications.placement.title': 'Tipo de publicación',
    'publications.placement.hint': 'Elegido según el contenido. Cámbialo si quieres otro.',
    'publications.byHand.label': 'La publico yo a mano',
    'publications.byHand.hint': 'La app no tocará la red: a su hora te la deja lista en «Publicar hoy».',

    // ── when ──
    'publications.when.title': 'Cuándo',
    'publications.when.date': 'Fecha',
    'publications.when.time': 'Hora',
    'publications.when.zone': 'Hora de {city}',
    'publications.when.summary': 'Sale el {when}',
    'publications.when.yours': 'Para ti: {when}',
    'publications.when.past': 'Esa hora ya ha pasado.',
    'publications.when.blocked': 'Ese día está bloqueado en el calendario: {reason}',
    'publications.when.blockedNoReason': 'Ese día está bloqueado en el calendario.',
    'publications.when.slots': 'Huecos libres de esta cuenta',
    'publications.when.slotUse': 'Usar el hueco del {when}',
    'publications.when.optional': 'Hora nueva (opcional)',
    'publications.when.optionalHint': 'Déjala vacía para mantener la hora original, o la más próxima si ya ha pasado.',

    // ── text ──
    'publications.text.title': 'Texto',
    'publications.text.label': 'Texto de la publicación',
    'publications.text.hint': 'El pie o la descripción para esta cuenta.',
    'publications.text.placeholder': 'Escribe el texto…',
    'publications.count.chars': 'Caracteres',
    'publications.count.graphemes': 'Caracteres (como se ven)',
    'publications.count.hashtags': 'Hashtags',
    'publications.count.mentions': 'Menciones',
    'publications.count.over': '{count} de más',
    'publications.feed.title': 'Así se ve en el feed',
    'publications.feed.more': 'más',
    'publications.feed.cut': 'Antes de «más» se ven los primeros {count} caracteres: pon ahí lo importante.',
    'publications.feed.fits': 'Se ve entero, sin «más».',
    'publications.feed.empty': 'Aquí verás cómo queda el texto en el feed.',
    'publications.firstComment.label': 'Primer comentario',
    'publications.firstComment.hint': 'Sale justo después del post, como comentario de la propia cuenta.',
    'publications.firstComment.none': '{network} no tiene primer comentario.',
    'publications.optional': '(opcional)',

    // ── issues ──
    'publications.issue.error': 'Impide publicar',
    'publications.issue.warning': 'Aviso',
    'publications.issue.list': 'Lo que ve la app antes de programarla',

    // ── the network's settings ──
    'publications.opt.title': 'Ajustes de {network}',
    'publications.opt.asking': 'Preguntando a {network} qué puede publicar esta cuenta…',
    'publications.opt.group.audience': 'Quién la ve',
    'publications.opt.group.details': 'Detalles',
    'publications.opt.group.interactions': 'Qué se permite',
    'publications.opt.group.commercial': 'Contenido comercial',
    'publications.opt.group.consent': 'Conformidad',
    'publications.opt.required': 'obligatorio',
    'publications.opt.choose': 'Elige…',
    'publications.opt.notSet': 'Sin indicar',
    'publications.opt.pick': 'Sin elegir: elígelo tú.',
    'publications.opt.off': 'Desactivado en la cuenta',

    // ── move or edit ──
    'publications.move.title': 'Mover o editar',
    'publications.move.confirmNeeded': 'Esta marca pide que otro aprobador confirme cualquier cambio en algo ya programado.',
    'publications.move.saved': 'Guardado',
    'publications.move.savedConfirm': 'Guardado: otro aprobador tiene que confirmarlo',

    // ── mark as published ──
    'publications.mark.title': 'Marcar como publicada',
    'publications.mark.body': 'Confirma que ya está publicada en la red. Si tienes el enlace, pégalo para que quede registrado.',
    'publications.mark.link': 'Enlace al post',
    'publications.mark.placeholder': 'https://',
    'publications.mark.submit': 'Marcar como publicada',
    'publications.mark.done': 'Marcada como publicada',

    // ── put back on the calendar ──
    'publications.reschedule.title': 'Volver a programar',
    'publications.reschedule.body': 'Una versión nueva sustituyó a la que estaba programada. Elige la versión aprobada que se publicará.',
    'publications.reschedule.none': 'Ninguna versión de esta variante está aprobada todavía. Aprueba antes la nueva.',
    'publications.reschedule.version': 'Versión',
    'publications.reschedule.versionN': 'Versión {n}',
    'publications.reschedule.latest': 'la más reciente',
    'publications.reschedule.submit': 'Reprogramar',
    'publications.reschedule.done': 'De vuelta en el calendario',

    // ── what the app did ──
    'publications.attempts.title': 'Qué ha hecho la app',
    'publications.attempts.empty': 'Nada todavía: empieza a prepararla poco antes de la hora.',
    'publications.attempts.try': 'intento {n}',
    'publications.attempts.ok': 'Hecho',
    'publications.attempts.pending': 'Esperando',
    'publications.attempts.error': 'Falló',
    'publications.attempts.http': 'HTTP {status}',
    'publications.attempts.visibility': 'Visibilidad: {visibility}',
    'publications.attempts.retryIn': { one: 'Reintenta en {count} minuto', other: 'Reintenta en {count} minutos' },
    'publications.attempts.footnote': 'Cada intento queda registrado, con lo que respondió la red. Los tokens nunca se guardan aquí.',

    // ── try again ──
    'publications.retry.title': 'Reintentar',
    'publications.retry.body': 'La app vuelve a empezar con la misma versión aprobada. Si la hora ya ha pasado, sale en un par de minutos.',
    'publications.retry.lastError': 'Último error',
    'publications.retry.submit': 'Reintentar',
    'publications.retry.done': 'Programada otra vez',

    // ── publish by hand ──
    'publications.pack.title': 'Publicar a mano',
    'publications.pack.files': 'Archivos',
    'publications.pack.noFiles': 'Esta versión no tiene archivos que descargar.',
    'publications.pack.text': 'Texto',
    'publications.pack.noText': 'Sin texto',
    'publications.pack.firstComment': 'Primer comentario',
    'publications.pack.published': 'Ya la he publicado…',
    'publications.pack.download': 'Descargar {name}',
    'publications.pack.file.video': 'Vídeo',
    'publications.pack.file.image': 'Imagen {n}',
    'publications.pack.file.cover': 'Portada',
    'publications.pack.file.subtitles': 'Subtítulos',
    'publications.pack.file.pdf': 'PDF',

    // ── a row's notes and badges ──
    'publications.mode.auto': 'Automática',
    'publications.mode.autoHint': 'La publica la app',
    'publications.mode.manual': 'A mano',
    'publications.mode.manualHint': 'La publica una persona',
    'publications.note.native': 'Ya está en la red, que la guarda hasta su hora',
    'publications.note.private.youtube': 'Subido como privado: Google deja privadas las subidas nuevas hasta que el proyecto pasa su auditoría',
    'publications.note.private.tiktok': 'Publicado como privado: TikTok deja privados los posts de una app hasta que la audita. Alguien puede hacerlo público en TikTok',
    'publications.note.private.pinterest': 'Fijado, pero nadie más lo ve hasta que Pinterest dé a la app el acceso Standard',
    'publications.note.private.other': 'Publicado como privado: solo lo ve la cuenta',
    'publications.note.processing': 'La red todavía lo está procesando',
    'publications.note.gone': 'La red ya no muestra este post',
    'publications.list.group.attention': 'Necesitan atención',
    'publications.list.group.upcoming': 'Próximas',
    'publications.list.group.published': 'Publicadas',
    'publications.list.group.cancelled': 'Canceladas',
    'publications.list.format.carousel': 'Carrusel',
    'publications.list.format.document': 'PDF',
    'publications.list.carries': 'Lleva la {version}',
    'publications.list.autoHint': 'Automática: la publica la app sola, a su hora',
    'publications.list.manualHint': 'A mano: a su hora, alguien la publica en la red y la marca como publicada',
    'publications.list.menu': 'Más acciones de la publicación en {account}',
    'publications.list.held': 'En espera hasta que se apruebe la versión nueva',
    'publications.list.awaiting': 'Se ha cambiado la fecha o el texto: otra persona tiene que confirmar el cambio',
    'publications.list.dueByHand': 'Ya es la hora: hay que publicarla a mano y marcarla como publicada',
    'publications.list.more': 'Ver más',
    'publications.list.less': 'Ver menos',
    'publications.list.retry': 'Reintentar…',
    'publications.list.publish': 'Publicar…',
    'publications.list.confirm': 'Confirmar el cambio',
    'publications.list.confirmHint': 'Tiene que confirmarlo alguien que no lo haya cambiado (tú eres {email})',
    'publications.list.reschedule': 'Reprogramar…',
    'publications.list.open': 'Ver el post',
    'publications.list.openHint': 'Abre el post en {network}',
    'publications.list.move': 'Cambiar fecha o texto…',
    'publications.list.recheck': 'Volver a mirar si es público',
    'publications.list.handOver': 'Publicarla a mano',
    'publications.list.handOverTitle': '¿Publicarla a mano?',
    'publications.list.handOverText': 'La app deja de intentar publicar en {account} ({network}). A su hora la tendrás en «Publicar hoy», con los ficheros y el texto listos para copiar.',
    'publications.list.handOverGo': 'Sí, la publico yo',
    'publications.list.prize': 'Premio…',
    'publications.list.history': 'Historial de intentos',
    'publications.list.cancel': 'Cancelar la publicación',
    'publications.list.cancelTitle': '¿Cancelar esta publicación?',
    'publications.list.cancelText': 'No saldrá en {account} ({network}) el {when}. Para volver a programarla tendrás que hacerlo desde la revisión.',
    'publications.list.cancelTextNative': 'No saldrá en {account} ({network}) el {when}, y se retira de la red, que ya la tenía guardada. Para volver a programarla tendrás que hacerlo desde la revisión.',
    'publications.list.cancelGo': 'Cancelar la publicación',
    'publications.list.done.cancel': 'Publicación cancelada',
    'publications.list.done.confirm': 'Cambio confirmado',
    'publications.list.done.handOver': 'Pasada a mano: la tienes en «Publicar hoy» a su hora',
    'publications.list.done.recheck': 'Volviendo a mirar el post',
  },
  en: {
    // ── schedule ──
    'publications.schedule.title': 'Schedule a publication',
    'publications.schedule.submit': 'Schedule',
    'publications.schedule.submitting': 'Scheduling…',
    'publications.schedule.doneAuto': 'Scheduled: the app will publish it',
    'publications.schedule.doneManual': 'Scheduled: someone has to publish it',
    'publications.schedule.blocked': { one: '{count} thing blocks publishing', other: '{count} things block publishing' },
    'publications.schedule.warnings': { one: '{count} heads-up', other: '{count} heads-ups' },
    'publications.schedule.blockedHint': 'Fix what blocks publishing, or choose to publish it by hand',
    'publications.schedule.needsWhen': 'Pick the date and time',

    'publications.account.title': 'Account',
    'publications.account.hint': 'Only the accounts this version was approved for are listed.',
    'publications.account.none': 'This version is not approved for any account yet. Approve it for an account and you can schedule it.',
    'publications.account.auto': 'Automatic',
    'publications.account.manual': 'By hand',
    'publications.account.reconnect': 'Needs reconnecting',

    'publications.plan.autoTitle': 'The app publishes this',
    'publications.plan.autoAs': 'as {placement}',
    'publications.plan.autoBody': 'It prepares the files shortly before the time and checks the post afterwards.',
    'publications.plan.manualTitle': 'A person publishes this',
    'publications.plan.manualBody': 'At its hour it shows up on Publish today, with the files and text ready to copy.',
    'publications.plan.checking': 'Checking how it would go out…',
    'publications.placement.title': 'Kind of post',
    'publications.placement.hint': 'Picked from the content. Change it if you want another.',
    'publications.byHand.label': 'I will publish this one by hand',
    'publications.byHand.hint': 'The app will not touch the network: at the hour it has it ready for you on Publish today.',

    // ── when ──
    'publications.when.title': 'When',
    'publications.when.date': 'Date',
    'publications.when.time': 'Time',
    'publications.when.zone': '{city} time',
    'publications.when.summary': 'Goes out {when}',
    'publications.when.yours': 'Your time: {when}',
    'publications.when.past': 'That time has already passed.',
    'publications.when.blocked': 'That day is blocked in the calendar: {reason}',
    'publications.when.blockedNoReason': 'That day is blocked in the calendar.',
    'publications.when.slots': 'Free slots for this account',
    'publications.when.slotUse': 'Use the slot on {when}',
    'publications.when.optional': 'New time (optional)',
    'publications.when.optionalHint': 'Leave it empty to keep the original time, or the soonest possible if that has passed.',

    // ── text ──
    'publications.text.title': 'Text',
    'publications.text.label': 'Text of the post',
    'publications.text.hint': 'The caption or description for this account.',
    'publications.text.placeholder': 'Write the text…',
    'publications.count.chars': 'Characters',
    'publications.count.graphemes': 'Characters (as seen)',
    'publications.count.hashtags': 'Hashtags',
    'publications.count.mentions': 'Mentions',
    'publications.count.over': '{count} over',
    'publications.feed.title': 'How the feed shows it',
    'publications.feed.more': 'more',
    'publications.feed.cut': 'Readers see the first {count} characters before tapping “more”. Put the point there.',
    'publications.feed.fits': 'It shows in full, with no “more”.',
    'publications.feed.empty': 'Here you will see how the text looks in the feed.',
    'publications.firstComment.label': 'First comment',
    'publications.firstComment.hint': 'Posted right after, as a comment from the account itself.',
    'publications.firstComment.none': '{network} has no first comment.',
    'publications.optional': '(optional)',

    // ── issues ──
    'publications.issue.error': 'Blocks publishing',
    'publications.issue.warning': 'Heads up',
    'publications.issue.list': 'What the app sees before scheduling it',

    // ── the network's settings ──
    'publications.opt.title': 'Settings for {network}',
    'publications.opt.asking': 'Asking {network} what this account can post…',
    'publications.opt.group.audience': 'Who sees it',
    'publications.opt.group.details': 'Details',
    'publications.opt.group.interactions': 'What is allowed',
    'publications.opt.group.commercial': 'Commercial content',
    'publications.opt.group.consent': 'Agreement',
    'publications.opt.required': 'required',
    'publications.opt.choose': 'Choose…',
    'publications.opt.notSet': 'Not set',
    'publications.opt.pick': 'Nothing chosen: pick it yourself.',
    'publications.opt.off': 'Off for this account',

    // ── move or edit ──
    'publications.move.title': 'Move or edit',
    'publications.move.confirmNeeded': 'This brand needs a second approver to confirm any change to something already scheduled.',
    'publications.move.saved': 'Saved',
    'publications.move.savedConfirm': 'Saved: another approver has to confirm it',

    // ── mark as published ──
    'publications.mark.title': 'Mark as published',
    'publications.mark.body': 'Confirm it is live on the network. Paste the link if you have it, so it stays on record.',
    'publications.mark.link': 'Link to the post',
    'publications.mark.placeholder': 'https://',
    'publications.mark.submit': 'Mark as published',
    'publications.mark.done': 'Marked as published',

    // ── put back on the calendar ──
    'publications.reschedule.title': 'Put back on the calendar',
    'publications.reschedule.body': 'A new version replaced the one this was scheduled with. Pick the approved version to publish.',
    'publications.reschedule.none': 'No version of this variant is approved yet. Approve the new one first.',
    'publications.reschedule.version': 'Version',
    'publications.reschedule.versionN': 'Version {n}',
    'publications.reschedule.latest': 'the latest',
    'publications.reschedule.submit': 'Reschedule',
    'publications.reschedule.done': 'Back on the calendar',

    // ── what the app did ──
    'publications.attempts.title': 'What the app did',
    'publications.attempts.empty': 'Nothing yet: it starts preparing shortly before the scheduled time.',
    'publications.attempts.try': 'try {n}',
    'publications.attempts.ok': 'Done',
    'publications.attempts.pending': 'Waiting',
    'publications.attempts.error': 'Failed',
    'publications.attempts.http': 'HTTP {status}',
    'publications.attempts.visibility': 'Visibility: {visibility}',
    'publications.attempts.retryIn': { one: 'Retrying in {count} minute', other: 'Retrying in {count} minutes' },
    'publications.attempts.footnote': 'Every try is kept for the record, with what the network answered. Tokens are never stored here.',

    // ── try again ──
    'publications.retry.title': 'Try again',
    'publications.retry.body': 'The app starts over with the same approved version. If the time has passed, it goes out in a couple of minutes.',
    'publications.retry.lastError': 'Last error',
    'publications.retry.submit': 'Try again',
    'publications.retry.done': 'Scheduled again',

    // ── publish by hand ──
    'publications.pack.title': 'Publish by hand',
    'publications.pack.files': 'Files',
    'publications.pack.noFiles': 'This version has no files to download.',
    'publications.pack.text': 'Text',
    'publications.pack.noText': 'No text',
    'publications.pack.firstComment': 'First comment',
    'publications.pack.published': 'I published it…',
    'publications.pack.download': 'Download {name}',
    'publications.pack.file.video': 'Video',
    'publications.pack.file.image': 'Image {n}',
    'publications.pack.file.cover': 'Cover',
    'publications.pack.file.subtitles': 'Subtitles',
    'publications.pack.file.pdf': 'PDF',

    // ── a row's notes and badges ──
    'publications.mode.auto': 'Automatic',
    'publications.mode.autoHint': 'The app publishes this',
    'publications.mode.manual': 'By hand',
    'publications.mode.manualHint': 'A person publishes this',
    'publications.note.native': 'Already with the network, which holds it until the hour',
    'publications.note.private.youtube': 'Uploaded as private: Google keeps new uploads private until the project passes its audit',
    'publications.note.private.tiktok': "Posted as private: TikTok keeps an app's posts private until it audits the app. Someone can make it public in TikTok",
    'publications.note.private.pinterest': 'Pinned, but not visible to others until Pinterest grants the app Standard access',
    'publications.note.private.other': 'Posted as private: only the account can see it',
    'publications.note.processing': 'The network is still processing it',
    'publications.note.gone': 'The network no longer shows this post',
    'publications.list.group.attention': 'Need attention',
    'publications.list.group.upcoming': 'Upcoming',
    'publications.list.group.published': 'Published',
    'publications.list.group.cancelled': 'Cancelled',
    'publications.list.format.carousel': 'Carousel',
    'publications.list.format.document': 'PDF',
    'publications.list.carries': 'Carries {version}',
    'publications.list.autoHint': 'Automatic: the app publishes it by itself, at its time',
    'publications.list.manualHint': 'By hand: at its time, someone publishes it on the network and marks it as published',
    'publications.list.menu': 'More actions for the publication on {account}',
    'publications.list.held': 'On hold until the new version is approved',
    'publications.list.awaiting': 'The date or the text changed: someone else has to confirm the change',
    'publications.list.dueByHand': 'It is time: it has to be published by hand and marked as published',
    'publications.list.more': 'Show more',
    'publications.list.less': 'Show less',
    'publications.list.retry': 'Try again…',
    'publications.list.publish': 'Publish…',
    'publications.list.confirm': 'Confirm the change',
    'publications.list.confirmHint': 'Someone who did not make the change has to confirm it (you are {email})',
    'publications.list.reschedule': 'Reschedule…',
    'publications.list.open': 'Open the post',
    'publications.list.openHint': 'Opens the post on {network}',
    'publications.list.move': 'Change date or text…',
    'publications.list.recheck': 'Check again whether it is public',
    'publications.list.handOver': 'Publish it by hand',
    'publications.list.handOverTitle': 'Publish it by hand?',
    'publications.list.handOverText': 'The app stops trying to publish on {account} ({network}). At its time you will find it on the Publish page, with the files and the text ready to copy.',
    'publications.list.handOverGo': 'Yes, I will publish it',
    'publications.list.prize': 'Prize…',
    'publications.list.history': 'Attempt history',
    'publications.list.cancel': 'Cancel the publication',
    'publications.list.cancelTitle': 'Cancel this publication?',
    'publications.list.cancelText': 'It will not go out on {account} ({network}) on {when}. To schedule it again you will have to do it from the review.',
    'publications.list.cancelTextNative': 'It will not go out on {account} ({network}) on {when}, and it is taken down from the network, which already held it. To schedule it again you will have to do it from the review.',
    'publications.list.cancelGo': 'Cancel the publication',
    'publications.list.done.cancel': 'Publication cancelled',
    'publications.list.done.confirm': 'Change confirmed',
    'publications.list.done.handOver': 'Handed over: it is on the Publish page at its time',
    'publications.list.done.recheck': 'Checking the post again',
  },
});
